import React, { useState, useEffect, useCallback } from 'react';
import validate from 'validate.js';
import schema from './schema';
import pages from '../../../../routes/pages';
import { useMutation } from '@apollo/client';
import LOGIN from '../../../../graphql/mutation/user/auth/login';
import { boundSetFeedback } from '../../../../store/actions/feedback.actions';
import goToPage from '../../../../helpers/page/goToPage';
import {
  storeAuthData,
  storeRoleType
} from '../../../../helpers/user/authorization';
import { TooManyLoginBlock } from 'components/molecules/Blocks/TooManyLoginBlock';
import { unloggedStrapiAxios } from '../../../../REST/strapiAxios';
import { capture } from '../../../../services/sentry/capture';
import { OnLoginMutation } from '../../../../graphql/generated/graphql';
import { LoginLayout } from 'components/layouts/LoginLayout';
import { SignInContent } from 'components/organisms/Contents/SignInContent';
import toast from 'react-hot-toast';
import { getGreetings } from '../../../../helpers/getGreetings';
import { ExternalLink } from 'components/atoms/Links/ExternalLink';
import { clearFeedback } from '../../../../store/actions/feedback.actions';
import { useDispatch } from 'react-redux';

interface FormType {
  values: {
    [key: string]: string;
  };
  touched: {
    [key: string]: boolean;
  };
  errors: {
    [key: string]: Record<string, unknown>;
  };
  isValid: boolean;
  disabled: boolean;
  errorCount: number;
  reveal: boolean;
}
const initialState: FormType = {
  values: {
    email: '',
    password: ''
  },
  touched: {
    email: false,
    password: false
  },
  errors: {
    email: null,
    password: null
  },
  isValid: false,
  disabled: false,
  errorCount: 0,
  reveal: false
};

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const handleClick = async () => {
    dispatch(clearFeedback());
    goToPage(pages.REINITIALISER.path);
  };
  const [form, setForm] = useState<FormType>(initialState);
  const [onLogin] = useMutation<OnLoginMutation>(LOGIN, {
    onCompleted: ({ login: { jwt, user } }) => {
      setForm(initialState);
      const greetings = getGreetings();
      toast(greetings.message, {
        icon: greetings.icon
      });
      storeAuthData({
        jwt,
        userId: user.id,
        username: user.username
      });
      unloggedStrapiAxios
        .get('/users-permissions/users/getRole/' + user.id)
        .then(res => {
          console.log('res', res.data);
          storeRoleType(res.data);
          return setTimeout(() => window.location.assign('/'), 2000);
        })
        .catch(e => {
          capture(new Error('Login : Role Introuvable'), [e, user]);
          console.log('Role introuvable');
          storeRoleType('eleve');
          return setTimeout(() => window.location.assign('/'), 1000);
        });
    },
    onError: e => {
      setForm({ ...form, disabled: true, isValid: false });
      if (form.errorCount >= 2) {
        boundSetFeedback({
          modal: {
            title: '3 tentatives infructeuses',
            actionButton: (
              <ExternalLink
                isOpenNewTab={true}
                variant="link"
                textInside="Lien vers le formulaire"
                link="https://forms.zohopublic.eu/francechallenges1/form/Contact1/formperma/-7TJC2HwzBlIxL9RsVc_XbgGRbbnKJRkv-FlrFFnCy0"
              />
            )
          },
          message: <TooManyLoginBlock buttonClick={handleClick} />
        });
      }
      toast.error(e.message);
      return setTimeout(() => {
        setForm({ ...initialState, errorCount: form.errorCount + 1 });
      }, 1000 * 2);
    }
  });

  const handleFieldChange = (field: string, value: string): void => {
    const values = { ...form.values, [field]: value };
    const errors = validate(values, schema);
    setForm({
      ...form,
      touched: { [field]: true },
      values,
      errors: errors || {},
      isValid: !errors
    });
  };

  const handleSignIn = useCallback(async () => {
    const { disabled, values } = form;
    if (!disabled && values.email !== '' && values.password !== '') {
      setLoading(true);
      await onLogin({
        variables: { identifier: values.email, password: values.password }
      });
      setLoading(false);
    }
  }, [form, onLogin]);

  useEffect(() => {
    const listener = event => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        handleSignIn();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [form.values, handleSignIn]);

  const { values, touched, errors } = form;
  const showEmailError = touched.email && errors.email;
  const showPasswordError = touched.password && errors.password;

  return (
    <LoginLayout>
      <SignInContent
        mailValue={values.email}
        onChangeMailValue={event =>
          handleFieldChange('email', event.target.value)
        }
        passwordValue={values.password}
        onChangePasswordValue={event =>
          handleFieldChange('password', event.target.value)
        }
        onClickConnect={handleSignIn}
        mailError={showEmailError && errors.email[0]}
        passwordError={showPasswordError && errors.password[0]}
        passwordLostLink={pages.REINITIALISER.path}
        createAccountLink={pages.SIGN_UP.path}
        isValid={form.isValid}
        loading={loading}
      />
    </LoginLayout>
  );
};

export default SignIn;
